import React, { useState } from "react";
import "./sustainability.css";
import stairs from "../../assets/images/trappa.webp";
import SEOTags from "../../utils/SEOTags";
import Layout from "../../components/layout/Layout";
import useCheckIfMobile from "../../customHooks/useCheckIfMobile";
import { ReadMoreButton } from "../../components/Buttons/ReadMoreButton/ReadMoreButton";

const BreadcrumbListSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      item: { "@id": "https://www.vakansa.se/", name: "Hem" },
    },
    {
      "@type": "ListItem",
      position: 2,
      item: {
        "@id": `https://www.vakansa.se/hallbarhet`,
        name: "Hållbarhet inom fastighetsbranschen",
      },
    },
  ],
};

export default function Sustainability() {
  const isMobile = useCheckIfMobile();
  const [expandedSections, setExpandedSections] = useState({});

  const isSectionOne = expandedSections["section1"] || false;
  const isSectionTwo = expandedSections["section2"] || false;
  const isSectionThree = expandedSections["section3"] || false;

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      <SEOTags
        title={"Vakansas hållbarhetsarbete och att skapa hållbara lokaler!"}
        description={
          "Vakansas hållbarhetsarbete handlar om att få bygg- & fastighetsbranschen att sluta riva & bygga nytt - genom att använda byggnader effektivare!"
        }
        canonicalUrl={"/hallbarhet"}
        ogImage={"/images/logo/meta-logo.jpg"}
        structuredData={BreadcrumbListSchema}
      />
      <Layout>
        <main className="sustainability-container">
          <h1>Hållbarhet inom fastighetsbranschen</h1>
          <div>
            <h2 className="undertitle">
              Vakansas bidrag till en hållbar omställning
            </h2>
            <p>
              I en tid då hållbarhet och effektiv resursanvändning är centrala
              frågor för samhällsutvecklingen, spelar fastighetsbranschen en
              avgörande roll. Städer utgör enbart 3% av jordens landyta men
              använder 60-80% av all energi som används, släpper ut 75% av
              utsläppen (
              <a href="https://www.un.org/sustainabledevelopment/cities/">
                Källa: FN
              </a>
              ) och står för cirka 50% av all råvaruuttag i världen (
              <a href="https://www.industrytransition.org/insights/towards-a-sustainable-global-construction-and-buildings-value-chain/">
                Källa: EU
              </a>
              ).
            </p>
            {isMobile && !isSectionOne ? (
              <ReadMoreButton
                isExpanded={isSectionOne}
                onToggle={() => toggleSection("section1")}
              />
            ) : (
              <>
                <div className="read-more-text">
                  <p>
                    I en värld där urbaniseringen antas öka markant de kommande
                    åren och decennierna blir behovet av att optimera
                    användningen av befintliga resurser allt viktigare. Särskilt
                    om vi inte vill se en fortsättning på rådande rivningsraseri
                    och överdriven nybyggnation, samtidigt som vi ser{" "}
                    <a href="https://beta.vakansa.se/blogg/hyra-lokal-n%C3%A4r-vakanserna-%C3%A4r-h%C3%B6ga">
                      {" "}
                      stora vakanser efter pandemin och lågkonjunktur.
                    </a>{" "}
                    Därför har vi på Vakansa tagit fram nedan modell, som vi
                    kallar för Fastighetstrappan. En trappa som visar det
                    ohållbara trappsteget som fastighetsbranschen befinner sig
                    idag och vilka steg som kan tas för att ta oss mot en
                    hållbar omställning. En trappa som tagit inspiration från{" "}
                    <a href="https://www.naturvardsverket.se/amnesomraden/avfall/pagaende-arbeten/avfallshierarkin-visar-stegen-vi-behover-ta/">
                      EU's avfallstrappa
                    </a>{" "}
                    och{" "}
                    <a href="https://bransch.trafikverket.se/for-dig-i-branschen/Planera-och-utreda/Planerings--och-analysmetoder/fyrstegsprincipen/">
                      Trafikverkets fyrstegsprincip.
                    </a>
                  </p>
                </div>
                {isMobile && (
                  <ReadMoreButton
                    isExpanded={isSectionOne}
                    onToggle={() => toggleSection("section1")}
                  />
                )}
              </>
            )}
          </div>
          <div className="sustainability-img">
            <img
              loading="lazy"
              className="stairs-image"
              src={stairs}
              alt="Hållbarhet, fastighetstrappan. Den mest hållbara byggnaden är den som aldrig behöver byggas"
              height={444}
              width={800}
            />
          </div>
          <div>
            <ol type="1" className="number-list">
              <li>
                <h3>Riva för att bygga och bygga för att riva:</h3> Vi kan inte
                fortsätta riva välfungerande befintliga byggnader, som uppförts
                med en klimat och miljöskuld, för att i nästa skede bygga nya
                byggnader av råvaror. Råvaror som kommer av exploatering av
                människor, djur och natur i när och fjärran. Välfungerande
                byggnader som man antingen tycker är fula, inte genererar
                tillräckligt höga intäkter eller värden, har installationer som
                nått sin tekniska livslängd eller någon annan "urban legend",
                som vår grundare kallar det för - ursäkt för varför en byggnad
                ska rivas. Av denna anledning har Vakansa skrivit under
                kampanjen "
                <a href="https://www.mittskifte.org/petitions/krama-byggnaden">
                  Krama byggnaden
                </a>
                " som vår grundare initierade år 2022.
              </li>

              <li>
                <h3>Bygga hus av hus:</h3> Vi kan demontera byggnader och
                använda byggnadsdelarna till nya eller befintliga byggnader för
                att minimera avfallet från rivningar av uttjänta byggnader. Här
                jobbar vår grundare deltid i forskningsprojektet “Återhus - att
                bygga hus av hus” (<a href="https://aterhus.nu/">Återhus</a>
                ).
              </li>

              <li>
                <h3>Bygga på, bygga om, bygga till eller flytta byggnader:</h3>{" "}
                Ska resurskrävande ingrepp göras på byggnader ska det helst av
                allt handla om att bygga på, bygga om, bygga till befintliga
                byggnader eller göras insatser för att flytta dem i sin helhet.
                Ju mindre resurskrävande insatser, desto bättre!
              </li>

              <li>
                <h3>Förvalta byggnaden resurseffektivt:</h3>
                Den absolut bästa åtgärden är att ta hand om de byggnader som
                redan finns och sträva efter “evighetsförvaltning” som
                Stockholms Kooperativa Bostadsbolag kallar det för (
                <a href="https://www.skb.org/vad-ar-evighetsforvaltning/">
                  Vad är evighetsförvaltning? | SKB
                </a>
                ) i syfte att jobba långsiktigt med fastighetsförvaltning och
                underhållsarbetet. Ibland kräver det dock nya hållbara
                affärsmodeller och lösningar när det kommer till lokaluthyrning
                där befintliga byggnader även används över dygnets vakna timmar,
                istället för enbart 10% av tiden som med fallet med kontor och
                20% av tiden när det kommer till skolor. Ju mer byggnader
                används, desto mindre blir behovet av att bygga nytt och det är
                precis det{" "}
                <a href="https://www.vakansa.se/om-oss">
                  vår grundare vigt sin karriär
                </a>{" "}
                till att jobba med. Förutom att grunda Vakansa och föreläsa om
                Fastighetstrappan, har han även bidragit till rapporten
                "Lokaldelning som norm" som getts av Kungliga
                ingenjörsvetenskapsakademin (IVA) (
                <a href="https://www.iva.se/publicerat/rapport-resurseffektiva-lokaler-i-sverige/">
                  Rapport: Resurseffektiva lokaler i Sverige
                </a>{" "}
                - lokaldelning som norm)
              </li>
              <div>
                <p>
                  Potentialen är enorm och några tydliga indikationer är att:
                </p>
                <ul>
                  <li>
                    Klimatbesparingspotentialen för lokaler som samnyttjas
                    uppgår till 302,5 ton Co2e per 550 kvm kontorslokal{" "}
                    <a href="https://ivl.diva-portal.org/smash/get/diva2:1549547/FULLTEXT01.pdf">
                      (Källa från Miljöinstitutet IVL “Delningens potential”)
                    </a>
                  </li>
                  <li>
                    Resursbesparingspotentialen för lokaler som samnyttjas
                    uppgår till 559 ton resurser per 550 kvm kontorslokal{" "}
                    <a href="https://wwwvasakronanse.cdn.triggerfish.cloud/uploads/2018/10/materialrapport-hubben.pdf">
                      (Källa från Vasakronans bygge “Hubben” i Uppsala)
                    </a>
                  </li>
                </ul>
              </div>
              <li>
                <h3>
                  Den mest hållbara byggnaden är den som aldrig behöver byggas:
                </h3>
                I vår del av världen, där det finns ett överflöd av
                kvadratmeter, behöver vi tänka om kring hur vi använder de
                kvadratmeter som byggts. Inte att bygga fler kvadratmeter. Det
                är först när bygg- och fastighetsbranschen inser att “den mest
                hållbara byggnaden är den som aldrig behöver byggas”, som all
                kraft kommer läggas på att “den mest hållbara byggnaden ska bli
                den som redan står”. Detta är något som de tunga
                forskningsrapporterna Exponential roadmap (
                <a href="https://exponentialroadmap.org/exponential-roadmap/">
                  Exponential Roadmap
                </a>
                ) och Circularity gap report (
                <a href="https://www.circularity-gap.world/2022">CGR 2022</a>)
                lyft.
              </li>
            </ol>
          </div>

          <div>
            <h2>Hållbara lokaler genom att göra lokaldelning till norm</h2>
            <p>
              <a href="https://www.vakansa.se/blogg/vakansa-utmanar-branschens-ovilja-till-lokaldelning">
                Sedan Vakansa AB grundades 2019 har fokuset varit att utmana
                branschens ovilja att göra lokaldelning till norm.
              </a>{" "}
              Efter pandemin och restriktionerna har vi snabbt etablerat oss som
              en aktö§r som aktivt försökt främja delningsekonomin inom
              fastighetsbranschen. Genom vår plattform{" "}
              <a href="https://www.vakansa.se/">Vakansa.se</a> gör vi det
              möjligt för både fastighetsägare och hyresgäster att maximera
              utnyttjandet av kommersiella lokaler och andra verksamhetslokaler.
              Vi har allt från kontor, teatrar, behandlingsrum, biografer och
              coworking. Vi har bland annat fokuserat extra mycket på{" "}
              <a href="https://beta.vakansa.se/blogg/brist-p%C3%A5-idrottshallar">
                bristen av idrottshallar och gymnastiksalar
              </a>
              , samt skrivit flera debattartiklar om hur vi öppnar upp lokaler
              för fler genom delning. Vår slogan är att "Dela lokaler med andra,
              gör det billigare för alla!"
            </p>
          </div>
          <div>
            <h2>
              Vi synliggör och tillgängliggör "tidsvakanserna" när det kommer
              till lokaluthyrning
            </h2>
            <p>
              Vår unika nisch är dels att vi är Sveriges bredaste och främsta
              marknadsplats för andrahandslokaler, men också vårt fokus på att
              resurseffektivisera det befintliga beståndet. För att lyckas med
              har vår grundare Robin Rushdi Al-sálehi bland annat myntat ordet
              "tidsvakans" (definition: tider då befintliga hyresgäster inte
              nyttjar sina lokaler och som hade kunnat användas av andra
              hyresgäster). Ett ord som tidigare inte fanns och som han ansåg
              behövdes för att beskriva den ineffektivitet som kan uppstå i
              uthyrda lokaler. Lokaler som rent juridiskt och avtalsmässigt
              anses vara uthyrda 24/7, men i praktiken kan stå oanvända mer än
              90% av tiden.
            </p>
            {isMobile && !isSectionTwo ? (
              <ReadMoreButton
                isExpanded={isSectionTwo}
                onToggle={() => toggleSection("section2")}
              />
            ) : (
              <>
                <div className="read-more-text">
                  <p>
                    Genom att arbeta aktivt med tidsvakanserna som finns i olika
                    typer av lokaler, genom att synliggöra och tillgängliggöra
                    lokalerna för andra hyresgäster, kan lokalernas
                    nyttjandegrad och beläggningsgrad öka. På så sätt kan
                    behovet av nybyggnation minska rent teoretiskt.
                  </p>
                  <p>
                    Vakansas plattform erbjuder även omfattande kringtjänster
                    som rör allt från avtalshantering, nyckelhantering,
                    bokningsysstem, hjälp med deposition, försäkring till stöd
                    med administrativa uppgifter. Allt sådant som kan underlätta
                    för både första- och andrahandshyresgäster att minimera
                    risker och hantering av andrahandsuthyrning. Detta bryter
                    ner de traditionella barriärerna för uthyrning och öppnar
                    upp marknaden för fler hyresgäster, såsom omomsade aktörer,
                    vilket skapar en mer dynamisk, inkluderande och flexibel
                    uthyrningsmarknad.
                  </p>
                </div>
                {isMobile && (
                  <ReadMoreButton
                    isExpanded={isSectionTwo}
                    onToggle={() => toggleSection("section2")}
                  />
                )}
              </>
            )}
          </div>
          <div>
            <h2>
              Delningsekonomi är socialt, miljömässigt och ekonomiskt hållbart
            </h2>
            <p>
              Genom att erbjuda flexibla lösningar för lokaluthyrning och
              delning möjliggör <a href="https://www.vakansa.se/">Vakansa.se</a>{" "}
              för företag, föreningar och organisationer att anpassa sig till
              förändrade behov och förutsättningar över tid. Detta är särskilt
              viktigt i en tid av snabb förändring och osäkerhet, där
              flexibilitet och anpassningsförmåga är avgörande för att överleva
              och inte gå i konkurs på grund av höga lokalkostnader.
            </p>
            {isMobile && !isSectionThree ? (
              <ReadMoreButton
                isExpanded={isSectionThree}
                onToggle={() => toggleSection("section3")}
              />
            ) : (
              <>
                <div className="read-more-text">
                  <p>
                    På detta sätt bidrar Vakansa också indirekt till social
                    hållbarhet där en mångfald av verksamheter kan ta plats i
                    städerna och som bjuder in en mångfald av individer. Städer
                    där vi vill bidra till att liv och rörelse sker över dygnets
                    vakna timmar, istället för att platser blir helt öde efter
                    ett visst klockslag. Här ingår föreningslivet, kulturlivet
                    och civilsamhället som självklara sektorer som vi vill
                    underlätta för att hitta billiga, flexibla och passande
                    lokaler för. Det ligger i vårt DNA då det även var därför
                    Vakansa startades en gång i tiden - att skapa "skolan mitt i
                    byn" där skolan står öppen på kvällar och helger fylld av
                    aktiviteter som föreningar anordnar.
                  </p>
                  <p>
                    Lokaler ska användas, inte stå tomma och vårt mål är att
                    ingen förening, inget företag och ingen organisation ska
                    behöva stå eller falla på grund av en lokal. Alla former av
                    “tidsvakanser” kan och ska synliggöras, tillgängliggöras och
                    med fördel hyras eller hyras ut via{" "}
                    <a href="https://www.vakansa.se/">Vakansa.se</a>.
                  </p>
                  <p>
                    Ytterligare en viktig aspekt av Vakansas arbete är vårt
                    ständiga bidrag till regelverk, standarder och aktivt bidra
                    till forum inom hållbar stadsutveckling och förvaltning. Vi
                    deltar idag i forum såsom{" "}
                    <a href="https://viablecities.se/om/medlemmar/">
                      Viable Cities
                    </a>
                    , <a href="https://www.shiftsweden.se/">ShiftSweden</a> och{" "}
                    <a href="https://byggforetagen.se/klimat/vi-ar-med/">
                      Färdplan för bygg och anläggning
                    </a>{" "}
                    för bygg och anläggning, driver Vakansa på för strukturella
                    förändringar inom branschen. Vårt arbete hjälper till att
                    forma en framtida fastighetsmarknad där delning och effektiv
                    resursanvändning står i centrum. Vi har till och med tagit
                    initiativet för{" "}
                    <a href="https://www.vakansa.se/blogg/delningsdagen-utses-den-24-januari">
                      "Delningsdagen" på den fattigaste dagen, som oftast
                      infaller den 24e januari.
                    </a>
                  </p>
                </div>
                {isMobile && (
                  <ReadMoreButton
                    isExpanded={isSectionThree}
                    onToggle={() => toggleSection("section3")}
                  />
                )}
              </>
            )}
          </div>
          <div>
            <h2>Nyckeln till hållbara lokaler stavas lokaldelning</h2>
            <p>
              Sammanfattningsvis representerar Vakansa en katalysator för
              förändring i fastighetsbranschen genom att effektivt koppla samman
              teknologi med hållbarhetsmål. Genom att digitalt matcha
              hyresgäster med varandra i en dedikerad marknadsplats för
              andrahandsuthyrning kan vi både visa på den potential som finns i
              att integrera delningsekonomiska principer i traditionella
              industrier och själva bidra till dess nyttor. Vakansas arbete är
              inte bara ett exempel på innovation utan också ett bevis på att
              fastighetsbranschen kan omformas till att vara en del av lösningen
              på dagens miljö- och klimatutmaningar.
            </p>
          </div>
        </main>
      </Layout>
    </>
  );
}
